import { useCallback, useEffect, useState } from "react";
import * as Styled from "./style";
import { VideoLegacySeasonTabContents } from "./video-legacy-season-tab-contents";
import { VideoLegacySeasonTabTitle } from "./video-legacy-season-tab-title";
import { GRAVURE_SELECT_TABS } from "~/components/blocks/product/const";
import type { VideoLegacySeasonType } from "~/components/blocks/product/type-guard";
import { G_VideoContentType } from "~/lib/graphql";
import { platform } from "~/lib/navigator/user-agent";
import { useAuth } from "~/provider/auth";

type VideoLegacySeasonTabProps = {
  videoLegacySeasonData: VideoLegacySeasonType;
  isAdultBlock: boolean;
};

/**
 * 作品末端 | グラビア | タブとそのタブに紐づくコンテンツを表示するコンポーネント
 * @param props {@link VideoLegacySeasonTabProps}
 */
export const VideoLegacySeasonTab = (props: VideoLegacySeasonTabProps) => {
  const { videoLegacySeasonData, isAdultBlock } = props;

  const {
    sampleMovie,
    content: {
      contentType,
      playInfo: { parts },
      viewingRights: { windowsURLSchemes, iosURLSchemes, androidURLSchemes },
    },
  } = videoLegacySeasonData;

  const { isLoggedIn } = useAuth();
  const [selectedTabValue, setSelectedTabValue] = useState("");

  const onChangeTabValue = useCallback((v: string) => {
    setSelectedTabValue(v);
  }, []);

  useEffect(() => {
    if (!isAdultBlock) {
      setSelectedTabValue(GRAVURE_SELECT_TABS.PRODUCT_INFO);
      return;
    }

    if (parts.length > 1 && contentType === G_VideoContentType.Vod_2D) {
      setSelectedTabValue(GRAVURE_SELECT_TABS.PART);
      return;
    }

    if (contentType !== G_VideoContentType.VodVr) {
      setSelectedTabValue(GRAVURE_SELECT_TABS.PRODUCT_INFO);
      return;
    }

    // これ以降のすべての処理は、contentType が VOD_VR の場合の処理

    if (!isLoggedIn) {
      setSelectedTabValue(GRAVURE_SELECT_TABS.PRODUCT_INFO);
      return;
    }

    // MEMO : ログイン済み かつ 未購入の場合は windowsURLSchemes が null で返却される
    if (platform === "Windows") {
      if (windowsURLSchemes !== undefined && windowsURLSchemes !== null && windowsURLSchemes.length > 0) {
        setSelectedTabValue(GRAVURE_SELECT_TABS.PART);
        return;
      }
    }

    // MEMO : ログイン済み かつ 未購入の場合は iosURLSchemes が null で返却される
    if (platform === "iOS") {
      if (iosURLSchemes !== undefined && iosURLSchemes !== null && iosURLSchemes.length > 0) {
        setSelectedTabValue(GRAVURE_SELECT_TABS.PART);
        return;
      }
    }

    // MEMO : ログイン済み かつ 未購入の場合は androidURLSchemes が null で返却される
    if (platform === "Android") {
      if (androidURLSchemes !== undefined && androidURLSchemes !== null && androidURLSchemes.length > 0) {
        setSelectedTabValue(GRAVURE_SELECT_TABS.PART);
        return;
      }
    }

    setSelectedTabValue(GRAVURE_SELECT_TABS.PRODUCT_INFO);
  }, []);

  return (
    <Styled.ArticleRootTab>
      <VideoLegacySeasonTabTitle
        selectedTabValue={selectedTabValue}
        onChangeTabValue={onChangeTabValue}
        sampleMovie={sampleMovie}
        contentType={contentType}
        totalPartLength={parts.length}
        totalWindowsURLSchemesLength={windowsURLSchemes?.length ?? 0}
        totalIosURLSchemesLength={iosURLSchemes?.length ?? 0}
        totalAndroidURLSchemesLength={androidURLSchemes?.length ?? 0}
        isAdultBlock={isAdultBlock}
      />

      <VideoLegacySeasonTabContents videoLegacySeasonData={videoLegacySeasonData} selectedTabValue={selectedTabValue} />
    </Styled.ArticleRootTab>
  );
};
